import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { memberService } from "../../../services";
import type { Member, MemberDraft, MemberStatus } from "../../../services/types";
import { PROOF_TYPES, MEMBER_STATUSES, PROOF_LABEL } from "../../../config/members";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { useConfirm } from "../../../context/ConfirmContext";
import { PageHeader, BreadcrumbItem } from "../../../components/feature/PageHeader";
import { Card } from "../../../components/base/Card";
import { Button } from "../../../components/base/Button";
import { TextField } from "../../../components/base/TextField";
import { Select } from "../../../components/base/Select";
import { Textarea } from "../../../components/base/Textarea";
import { Toggle } from "../../../components/base/Toggle";
import { Alert } from "../../../components/base/Alert";
import { formatDateTime } from "../../../utils/format";

/* ---------- validation helpers ---------- */

function normalizePhone(raw: string): string {
  return raw.replace(/[^0-9]/g, "").replace(/^(91|0)/, "");
}

const PHONE_RE = /^[6-9]\d{9}$/;
const PIN_RE = /^\d{6}$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const AADHAAR_RE = /^\d{12}$/;
const PAN_RE = /^[A-Z]{5}\d{4}[A-Z]$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function proofFormat(proofType: string): { hint: string; re: RegExp; message: string } | null {
  switch (proofType) {
    case "aadhaar":
      return { hint: "12 digits", re: AADHAAR_RE, message: "Aadhaar must be exactly 12 digits." };
    case "pan":
      return { hint: "5 letters, 4 digits, 1 letter (e.g. ABCDE1234F)", re: PAN_RE, message: "PAN must match the format ABCDE1234F." };
    case "voter":
      return { hint: "3 letters + 7 digits (e.g. ABC1234567)", re: /^[A-Z]{3}\d{7}$/, message: "Voter ID must be 3 letters followed by 7 digits." };
    case "driving":
      return { hint: "State code + 15 alphanumeric characters", re: /^[A-Z]{2}\d{2}[0-9A-Z]{10,15}$/, message: "Licence number looks invalid." };
    case "passport":
      return { hint: "1 letter + 7 digits (e.g. A1234567)", re: /^[A-Z]\d{7}$/, message: "Passport number must be 1 letter followed by 7 digits." };
    default:
      return null;
  }
}

const emptyDraft = (): MemberDraft => ({
  fullName: "",
  guardianName: "",
  primaryPhone: "",
  alternatePhone: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pinCode: "",
  proofType: "",
  proofNumber: "",
  bankAccountName: "",
  bankAccountNumber: "",
  ifsc: "",
  upiId: "",
  isOrganizer: false,
  status: "active",
  notes: "",
});

function toDraft(member: Member): MemberDraft {
  const { id: _id, memberCode: _code, enrollments: _e, documents: _doc, guarantors: _g, createdBy: _cb, createdAt: _ca, updatedBy: _ub, updatedAt: _ua, ...draft } = member;
  return draft;
}

interface FormErrors {
  fullName?: string;
  primaryPhone?: string;
  pinCode?: string;
  ifsc?: string;
  proofNumber?: string;
  email?: string;
}

export default function MemberFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { isViewer } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();

  const [draft, setDraft] = useState<MemberDraft>(emptyDraft);
  const [memberCode, setMemberCode] = useState("");
  const [audit, setAudit] = useState<{ createdBy: string; createdAt: string; updatedBy: string; updatedAt: string } | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [duplicate, setDuplicate] = useState<Member | null>(null);

  const setField = useCallback((key: keyof MemberDraft, value: string | boolean) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setTouched((t) => ({ ...t, [key]: true }));
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    let active = true;
    memberService
      .getMember(id)
      .then((m) => {
        if (!active) return;
        setDraft(toDraft(m));
        setMemberCode(m.memberCode);
        setAudit({ createdBy: m.createdBy, createdAt: m.createdAt, updatedBy: m.updatedBy, updatedAt: m.updatedAt });
      })
      .catch((err) => toast.error("Could not load member", err instanceof Error ? err.message : "Please try again."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [isEdit, id, toast]);

  // Live duplicate-phone check once a full 10-digit number is typed.
  useEffect(() => {
    const phone = normalizePhone(draft.primaryPhone);
    if (!PHONE_RE.test(phone)) {
      setDuplicate(null);
      return;
    }
    let active = true;
    memberService.getMemberByPhone(phone).then((match) => {
      if (!active) return;
      if (match && match.id !== id) setDuplicate(match);
      else setDuplicate(null);
    });
    return () => {
      active = false;
    };
  }, [draft.primaryPhone, id]);

  const validate = useCallback((d: MemberDraft): FormErrors => {
    const next: FormErrors = {};
    if (!d.fullName.trim()) next.fullName = "Full name is required.";
    const phone = normalizePhone(d.primaryPhone);
    if (!d.primaryPhone.trim()) next.primaryPhone = "Primary phone number is required.";
    else if (!PHONE_RE.test(phone)) next.primaryPhone = "Enter a valid 10-digit mobile number.";
    if (d.pinCode && !PIN_RE.test(d.pinCode.trim())) next.pinCode = "PIN code must be exactly 6 digits.";
    if (d.ifsc && !IFSC_RE.test(d.ifsc.trim().toUpperCase())) next.ifsc = "IFSC must be 4 letters, then 0, then 6 alphanumeric characters.";
    if (d.proofType && d.proofNumber) {
      const fmt = proofFormat(d.proofType);
      if (fmt && !fmt.re.test(d.proofNumber.trim().toUpperCase())) next.proofNumber = fmt.message;
    }
    if (d.email && !EMAIL_RE.test(d.email.trim())) next.email = "Enter a valid email address.";
    return next;
  }, []);

  // Live instant validation as user types into any field
  useEffect(() => {
    const allErrors = validate(draft);
    const visibleErrors: FormErrors = {};
    (Object.keys(allErrors) as (keyof FormErrors)[]).forEach((key) => {
      if (touched[key] || hasSubmitted) {
        visibleErrors[key] = allErrors[key];
      }
    });
    setErrors(visibleErrors);
  }, [draft, touched, hasSubmitted, validate]);

  const doSave = useCallback(
    async (keepOpen: boolean) => {
      if (submitting) return;
      setHasSubmitted(true);
      const validationErrors = validate(draft);
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) {
        toast.error("Please fix the highlighted fields.");
        return;
      }
      // Warn, don't block: confirm when the phone belongs to another member.
      if (duplicate && duplicate.id !== id) {
        const proceed = await confirm({
          title: "Phone number already in use",
          message: `${duplicate.fullName} (${duplicate.memberCode}) already has this number. Families sometimes share phones — save anyway?`,
          confirmLabel: "Save anyway",
          cancelLabel: "Go back",
        });
        if (!proceed) return;
      }
      setSubmitting(true); // disable immediately to prevent double submission
      const payload: MemberDraft = {
        ...draft,
        fullName: titleCase(draft.fullName.trim()),
        guardianName: titleCase(draft.guardianName.trim()),
        primaryPhone: normalizePhone(draft.primaryPhone),
        alternatePhone: draft.alternatePhone.trim(),
        email: draft.email.trim(),
        addressLine1: draft.addressLine1.trim(),
        addressLine2: draft.addressLine2.trim(),
        city: titleCase(draft.city.trim()),
        state: titleCase(draft.state.trim()),
        pinCode: draft.pinCode.trim(),
        proofType: draft.proofType,
        proofNumber: draft.proofNumber.trim().toUpperCase(),
        bankAccountName: draft.bankAccountName.trim(),
        bankAccountNumber: draft.bankAccountNumber.trim(),
        ifsc: draft.ifsc.trim().toUpperCase(),
        upiId: draft.upiId.trim(),
        notes: draft.notes.trim(),
      };
      const actor = "Staff User";
      try {
        if (isEdit && id) {
          const updated = await memberService.updateMember(id, payload, actor);
          toast.success("Member updated", `${updated.fullName} (${updated.memberCode}) was saved.`);
          navigate("/members");
        } else {
          await memberService.createMember(payload, actor);
          if (keepOpen) {
            // Retain address fields — members enrolled together share localities.
            const prev = { addressLine1: payload.addressLine1, addressLine2: payload.addressLine2, city: payload.city, state: payload.state, pinCode: payload.pinCode };
            toast.success("Member added", "Record saved. Add the next member.");
            setDraft({ ...emptyDraft(), ...prev });
            setDuplicate(null);
            setTouched({});
            setHasSubmitted(false);
            setErrors({});
            setSubmitting(false);
          } else {
            toast.success("Member added", "Record saved and returned to the list.");
            navigate("/members");
          }
        }
      } catch (err) {
        toast.error("Could not save member", err instanceof Error ? err.message : "Please try again.");
        setSubmitting(false);
      }
    },
    [submitting, validate, duplicate, id, isEdit, draft, confirm, toast, navigate]
  );

  const proofHint = useMemo(() => (draft.proofType ? proofFormat(draft.proofType) : null), [draft.proofType]);

  if (loading) {
    return (
      <div>
        <PageHeader title="Loading member…" />
        <div className="space-y-3">
          <div className="h-40 animate-pulse rounded-lg bg-background-200" />
          <div className="h-40 animate-pulse rounded-lg bg-background-200" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumb={<BreadcrumbItem label="Home" to="/" />}
        title={isEdit ? `Edit ${memberCode}` : "Add member"}
        subtitle={isEdit ? "Update this member's shared identity and contact details." : "Create a member record. Only name and phone are required — finish details later."}
      />

      <form onSubmit={(e) => { e.preventDefault(); doSave(false); }} className="space-y-4">
        {duplicate && duplicate.id !== id ? (
          <Alert variant="warning" title="Duplicate phone number detected">
            This number already belongs to{" "}
            <Link to={`/members/${duplicate.id}`} className="font-medium underline">
              {duplicate.fullName} ({duplicate.memberCode})
            </Link>
            . You can still save — families sometimes share phones.
          </Alert>
        ) : null}

        <Card title="Personal details">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <TextField label="Full name" required value={draft.fullName} onChange={(e) => setField("fullName", e.target.value)} error={errors.fullName} placeholder="e.g. Ramesh Gupta" />
            <TextField label="Father's / Spouse's name" value={draft.guardianName} onChange={(e) => setField("guardianName", e.target.value)} placeholder="e.g. Suresh Gupta" />
            <TextField label="Primary phone" required leftIcon="ri-phone-line" value={draft.primaryPhone} onChange={(e) => setField("primaryPhone", e.target.value)} error={errors.primaryPhone} placeholder="10-digit mobile" inputMode="numeric" />
            <TextField label="Alternate phone" leftIcon="ri-phone-line" value={draft.alternatePhone} onChange={(e) => setField("alternatePhone", e.target.value)} placeholder="Optional" inputMode="numeric" />
            <TextField label="Email address" type="email" value={draft.email} onChange={(e) => setField("email", e.target.value)} error={errors.email} placeholder="name@example.com" />
          </div>
        </Card>

        <Card title="Address">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <TextField label="Address line 1" value={draft.addressLine1} onChange={(e) => setField("addressLine1", e.target.value)} placeholder="House no., street" />
            <TextField label="Address line 2" value={draft.addressLine2} onChange={(e) => setField("addressLine2", e.target.value)} placeholder="Area, landmark" />
            <TextField label="City" value={draft.city} onChange={(e) => setField("city", e.target.value)} placeholder="City" />
            <TextField label="State" value={draft.state} onChange={(e) => setField("state", e.target.value)} placeholder="State" />
            <TextField label="PIN code" value={draft.pinCode} onChange={(e) => setField("pinCode", e.target.value)} error={errors.pinCode} placeholder="6 digits" inputMode="numeric" />
          </div>
        </Card>

        <Card title="Identity proof">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Select
              label="Proof type"
              value={draft.proofType || null}
              onChange={(v) => setField("proofType", v)}
              placeholder="Select proof type"
              options={PROOF_TYPES.map((p) => ({ value: p.value, label: p.label }))}
            />
            <TextField
              label="Proof number"
              value={draft.proofNumber}
              onChange={(e) => setField("proofNumber", e.target.value)}
              error={errors.proofNumber}
              helper={proofHint ? proofHint.hint : undefined}
              placeholder={draft.proofType ? PROOF_LABEL[draft.proofType] + " number" : "Select a proof type first"}
            />
          </div>
        </Card>

        <Card title="Bank details" subtitle="Used only for payout transfers. Optional — many members deal in cash.">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <TextField label="Account holder name" value={draft.bankAccountName} onChange={(e) => setField("bankAccountName", e.target.value)} placeholder="As per bank records" />
            <TextField label="Account number" value={draft.bankAccountNumber} onChange={(e) => setField("bankAccountNumber", e.target.value)} placeholder="Account number" />
            <TextField label="IFSC code" value={draft.ifsc} onChange={(e) => setField("ifsc", e.target.value)} error={errors.ifsc} placeholder="e.g. HDFC0001234" />
            <TextField label="UPI ID" value={draft.upiId} onChange={(e) => setField("upiId", e.target.value)} placeholder="name@bank" />
          </div>
        </Card>

        <Card title="Settings">
          <div className="space-y-5">
            <Toggle
              checked={draft.isOrganizer}
              onChange={(v) => setField("isOrganizer", v)}
              label="This person is an organizer"
              description="The organizer holds a ticket in their own groups and pays installments like everyone else."
            />
            <div className="max-w-xs">
              <Select
                label="Status"
                value={draft.status}
                onChange={(v) => setField("status", v as MemberStatus)}
                options={MEMBER_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
              />
            </div>
            <Textarea
              label="Notes"
              value={draft.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Internal notes about this member"
              maxLength={500}
              rows={3}
            />
          </div>
        </Card>

        {audit ? (
          <p className="text-xs text-foreground-500">
            Created by {audit.createdBy} on {formatDateTime(audit.createdAt)} · Last updated by {audit.updatedBy} on {formatDateTime(audit.updatedAt)}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-background-100 pt-4">
          <Button variant="outline" onClick={() => navigate("/members")} disabled={submitting}>
            Cancel
          </Button>
          {!isEdit ? (
            <Button type="button" variant="secondary" icon={<i className="ri-add-line" />} disabled={submitting} loading={submitting} onClick={() => doSave(true)}>
              Save &amp; add another
            </Button>
          ) : null}
          <Button type="submit" icon={<i className="ri-check-line" />} loading={submitting} disabled={isViewer || submitting}>
            {isEdit ? "Save changes" : "Save &amp; back to list"}
          </Button>
        </div>
      </form>
    </div>
  );
}